/**
 * SHARED WALL COORDINATE COLLISION PREVENTION SYSTEM
 * 
 * This module solves the core issue where connecting rooms create walls with identical 
 * coordinates that cause confusion during resize operations. The system ensures that:
 * 
 * 1. Walls with identical coordinates are properly identified as shared walls
 * 2. Room ownership is clearly established and maintained
 * 3. Resize operations can correctly identify which walls belong to which rooms
 * 4. No duplicate walls exist at the same coordinates
 */

import { Wall, Point, Room } from '../types';

export interface WallWithTempId extends Wall {
  tempId?: string;
  originatingRoomId?: string;
  assignedRoomIds?: string[];
  parentRoomIds?: string[];
  isSharedWall?: boolean;
  sharedWallGroupId?: string;
}

export interface SharedWallGroup {
  groupId: string;
  coordinateKey: string;
  primaryWall: WallWithTempId;
  linkedWalls: WallWithTempId[];
  ownerRoomIds: string[];
  createdAt: number;
}

export interface CoordinateCollisionResult {
  hasCollision: boolean;
  existingWalls: WallWithTempId[];
  shouldCreateSharedWall: boolean;
  shouldUseExistingWall: boolean;
  recommendedAction: 'create_new' | 'use_existing' | 'create_shared' | 'merge_ownership';
  sharedWallGroup?: SharedWallGroup;
}

/**
 * Core system class for managing shared wall coordinates
 */
export class SharedWallCoordinateSystem {
  private sharedWallGroups: Map<string, SharedWallGroup> = new Map();
  private coordinateToGroupMap: Map<string, string> = new Map();
  private wallToGroupMap: Map<string, string> = new Map();
  
  private readonly COORDINATE_TOLERANCE = 0.1;
  private readonly MAX_SHARED_WALLS_PER_COORDINATE = 4; // Maximum rooms that can share a wall

  /**
   * Generate a stable coordinate key for wall identification
   */
  private generateCoordinateKey(wall: Wall): string {
    const startX = Math.round(wall.start.x / this.COORDINATE_TOLERANCE) * this.COORDINATE_TOLERANCE;
    const startY = Math.round(wall.start.y / this.COORDINATE_TOLERANCE) * this.COORDINATE_TOLERANCE;
    const endX = Math.round(wall.end.x / this.COORDINATE_TOLERANCE) * this.COORDINATE_TOLERANCE;
    const endY = Math.round(wall.end.y / this.COORDINATE_TOLERANCE) * this.COORDINATE_TOLERANCE;
    
    // Always order coordinates consistently (smaller coordinate pair first)
    const coord1 = `${startX.toFixed(2)},${startY.toFixed(2)}`;
    const coord2 = `${endX.toFixed(2)},${endY.toFixed(2)}`;
    
    return coord1 < coord2 ? `${coord1}→${coord2}` : `${coord2}→${coord1}`;
  }

  /**
   * Check for coordinate collisions and determine the best action
   */
  public checkCoordinateCollision(
    newWall: WallWithTempId,
    existingWalls: WallWithTempId[],
    newRoomId: string
  ): CoordinateCollisionResult {
    const coordinateKey = this.generateCoordinateKey(newWall);
    const existingGroup = this.sharedWallGroups.get(this.coordinateToGroupMap.get(coordinateKey) || '');
    
    // Find walls with identical coordinates
    const wallsWithSameCoordinates = existingWalls.filter(wall => 
      this.generateCoordinateKey(wall) === coordinateKey
    );

    console.log('🔍 COORDINATE COLLISION CHECK:', {
      coordinateKey,
      newRoomId,
      existingWallsWithSameCoords: wallsWithSameCoordinates.length,
      hasExistingGroup: !!existingGroup,
      newWallCoords: `(${newWall.start.x.toFixed(1)},${newWall.start.y.toFixed(1)})->(${newWall.end.x.toFixed(1)},${newWall.end.y.toFixed(1)})`
    });

    if (wallsWithSameCoordinates.length === 0) {
      return {
        hasCollision: false,
        existingWalls: [],
        shouldCreateSharedWall: false,
        shouldUseExistingWall: false,
        recommendedAction: 'create_new'
      };
    }

    // Check if any existing wall already belongs to the same room
    const wallsFromSameRoom = wallsWithSameCoordinates.filter(wall => 
      wall.originatingRoomId === newRoomId || 
      wall.assignedRoomIds?.includes(newRoomId)
    );

    if (wallsFromSameRoom.length > 0) {
      console.log('🔄 SAME ROOM WALL EXISTS:', {
        existingWallIds: wallsFromSameRoom.map(w => w.tempId),
        roomId: newRoomId
      });
      
      return {
        hasCollision: true,
        existingWalls: wallsFromSameRoom,
        shouldCreateSharedWall: false,
        shouldUseExistingWall: true,
        recommendedAction: 'use_existing',
        sharedWallGroup: existingGroup
      };
    }

    // Check if we can add this room to existing shared wall group
    if (existingGroup && existingGroup.ownerRoomIds.length < this.MAX_SHARED_WALLS_PER_COORDINATE) {
      console.log('🔗 EXTENDING SHARED WALL GROUP:', {
        groupId: existingGroup.groupId,
        currentOwners: existingGroup.ownerRoomIds,
        newOwner: newRoomId
      });
      
      return {
        hasCollision: true,
        existingWalls: wallsWithSameCoordinates,
        shouldCreateSharedWall: false,
        shouldUseExistingWall: false,
        recommendedAction: 'merge_ownership',
        sharedWallGroup: existingGroup
      };
    }

    // Create new shared wall group
    console.log('🆕 CREATING NEW SHARED WALL GROUP:', {
      coordinateKey,
      existingWallCount: wallsWithSameCoordinates.length,
      newRoomId
    });

    return {
      hasCollision: true,
      existingWalls: wallsWithSameCoordinates,
      shouldCreateSharedWall: true,
      shouldUseExistingWall: false,
      recommendedAction: 'create_shared'
    };
  }

  /**
   * Create or update a shared wall group
   */
  public createOrUpdateSharedWallGroup(
    primaryWall: WallWithTempId,
    roomId: string,
    existingWalls: WallWithTempId[] = []
  ): SharedWallGroup {
    const coordinateKey = this.generateCoordinateKey(primaryWall);
    const groupId = `shared_${coordinateKey}_${Date.now()}`;
    
    // Ensure primary wall has proper ownership
    const enhancedPrimaryWall: WallWithTempId = {
      ...primaryWall,
      originatingRoomId: primaryWall.originatingRoomId || roomId,
      assignedRoomIds: primaryWall.assignedRoomIds || [roomId],
      isSharedWall: true,
      sharedWallGroupId: groupId
    };

    // Update existing walls to be part of the shared group
    const linkedWalls = existingWalls.map(wall => ({
      ...wall,
      isSharedWall: true,
      sharedWallGroupId: groupId,
      assignedRoomIds: [...(wall.assignedRoomIds || []), roomId].filter((id, index, arr) => arr.indexOf(id) === index)
    }));

    const ownerRoomIds = [
      roomId,
      ...existingWalls.flatMap(w => w.assignedRoomIds || []),
      ...existingWalls.map(w => w.originatingRoomId).filter(Boolean) as string[]
    ].filter((id, index, arr) => arr.indexOf(id) === index);

    const sharedGroup: SharedWallGroup = {
      groupId,
      coordinateKey,
      primaryWall: enhancedPrimaryWall,
      linkedWalls,
      ownerRoomIds,
      createdAt: Date.now()
    };

    // Update internal tracking
    this.sharedWallGroups.set(groupId, sharedGroup);
    this.coordinateToGroupMap.set(coordinateKey, groupId);
    this.wallToGroupMap.set(enhancedPrimaryWall.tempId || '', groupId);
    linkedWalls.forEach(wall => {
      if (wall.tempId) {
        this.wallToGroupMap.set(wall.tempId, groupId);
      }
    });

    console.log('✅ SHARED WALL GROUP CREATED:', {
      groupId,
      coordinateKey,
      ownerRoomIds,
      primaryWallId: enhancedPrimaryWall.tempId,
      linkedWallCount: linkedWalls.length
    });

    return sharedGroup;
  }

  /**
   * Merge ownership of an existing wall with a new room
   */
  public mergeWallOwnership(
    existingWall: WallWithTempId,
    newRoomId: string
  ): WallWithTempId {
    const currentAssignedRooms = existingWall.assignedRoomIds || [];
    
    if (currentAssignedRooms.includes(newRoomId)) {
      console.log('🔄 ROOM ALREADY OWNS WALL:', {
        wallId: existingWall.tempId,
        roomId: newRoomId,
        currentOwners: currentAssignedRooms
      });
      return existingWall;
    }

    const updatedWall: WallWithTempId = {
      ...existingWall,
      assignedRoomIds: [...currentAssignedRooms, newRoomId],
      isSharedWall: true
    };

    // Update shared wall group if it exists
    const groupId = this.wallToGroupMap.get(existingWall.tempId || '');
    if (groupId) {
      const group = this.sharedWallGroups.get(groupId);
      if (group) {
        group.ownerRoomIds = [...group.ownerRoomIds, newRoomId].filter((id, index, arr) => arr.indexOf(id) === index);
        this.sharedWallGroups.set(groupId, group);
      }
    }

    console.log('🔗 WALL OWNERSHIP MERGED:', {
      wallId: existingWall.tempId,
      newRoomId,
      updatedOwners: updatedWall.assignedRoomIds,
      isSharedWall: true
    });

    return updatedWall;
  }

  /**
   * Safe wall lookup that handles multiple walls at same coordinates
   */
  public findWallForRoom(
    coordinates: { start: Point; end: Point },
    roomId: string,
    allWalls: WallWithTempId[]
  ): WallWithTempId | undefined {
    const coordinateKey = this.generateCoordinateKey(coordinates as Wall);
    
    // First, try to find a wall that belongs to this specific room
    const wallsAtCoordinate = allWalls.filter(wall => 
      this.generateCoordinateKey(wall) === coordinateKey
    );

    // Prioritize walls that belong to the specific room
    const ownedByRoom = wallsAtCoordinate.find(wall =>
      wall.originatingRoomId === roomId || 
      wall.assignedRoomIds?.includes(roomId)
    );

    if (ownedByRoom) {
      console.log('🎯 FOUND ROOM-SPECIFIC WALL:', {
        wallId: ownedByRoom.tempId,
        roomId,
        coordinateKey
      });
      return ownedByRoom;
    }

    // If no room-specific wall found, return the first wall (with warning)
    if (wallsAtCoordinate.length > 0) {
      console.warn('⚠️ COORDINATE COLLISION: Multiple walls at same coordinates, using first wall', {
        coordinateKey,
        roomId,
        wallCount: wallsAtCoordinate.length,
        wallIds: wallsAtCoordinate.map(w => w.tempId)
      });
      return wallsAtCoordinate[0];
    }

    return undefined;
  }

  /**
   * Get all walls in a shared wall group
   */
  public getSharedWallGroup(wallId: string): SharedWallGroup | undefined {
    const groupId = this.wallToGroupMap.get(wallId);
    return groupId ? this.sharedWallGroups.get(groupId) : undefined;
  }

  /**
   * Cleanup unused shared wall groups
   */
  public cleanup(): void {
    const now = Date.now();
    const maxAge = 5 * 60 * 1000; // 5 minutes

    for (const [groupId, group] of this.sharedWallGroups) {
      if (now - group.createdAt > maxAge && group.ownerRoomIds.length === 0) {
        this.sharedWallGroups.delete(groupId);
        this.coordinateToGroupMap.delete(group.coordinateKey);
        
        console.log('🧹 CLEANED UP UNUSED SHARED WALL GROUP:', { groupId });
      }
    }
  }

  /**
   * Get diagnostic information about the shared wall system
   */
  public getDiagnostics() {
    return {
      totalSharedGroups: this.sharedWallGroups.size,
      coordinateMappings: this.coordinateToGroupMap.size,
      wallMappings: this.wallToGroupMap.size,
      groups: Array.from(this.sharedWallGroups.values()).map(group => ({
        groupId: group.groupId,
        coordinateKey: group.coordinateKey,
        ownerRoomIds: group.ownerRoomIds,
        wallCount: 1 + group.linkedWalls.length
      }))
    };
  }
}

// Global instance
export const sharedWallSystem = new SharedWallCoordinateSystem();

/**
 * Enhanced room creation helper that prevents coordinate collisions
 */
export function createConnectingRoomWithSharedWalls(
  newRoomWalls: WallWithTempId[],
  existingWalls: WallWithTempId[],
  roomId: string
): {
  processedWalls: WallWithTempId[];
  sharedWallGroups: SharedWallGroup[];
  coordinateCollisions: number;
} {
  const processedWalls: WallWithTempId[] = [];
  const sharedWallGroups: SharedWallGroup[] = [];
  let coordinateCollisions = 0;

  console.log('🏗️ CREATING CONNECTING ROOM WITH SHARED WALLS:', {
    roomId,
    newWallCount: newRoomWalls.length,
    existingWallCount: existingWalls.length
  });

  for (const newWall of newRoomWalls) {
    const collisionResult = sharedWallSystem.checkCoordinateCollision(
      newWall,
      existingWalls,
      roomId
    );

    if (!collisionResult.hasCollision) {
      // No collision - create wall normally with proper ownership
      const wallWithOwnership: WallWithTempId = {
        ...newWall,
        originatingRoomId: roomId,
        assignedRoomIds: [roomId]
      };
      processedWalls.push(wallWithOwnership);
      continue;
    }

    coordinateCollisions++;

    switch (collisionResult.recommendedAction) {
      case 'use_existing':
        // Use existing wall that already belongs to this room
        console.log('📎 REUSING EXISTING WALL FOR ROOM:', {
          roomId,
          existingWallId: collisionResult.existingWalls[0]?.tempId
        });
        // Don't add to processedWalls - wall already exists
        break;

      case 'merge_ownership':
        // Add this room to existing shared wall
        if (collisionResult.existingWalls.length > 0) {
          const existingWall = collisionResult.existingWalls[0];
          const mergedWall = sharedWallSystem.mergeWallOwnership(existingWall, roomId);
          
          // Update the wall in existing walls array (this would need to be handled by caller)
          console.log('🔄 MERGED OWNERSHIP:', {
            wallId: mergedWall.tempId,
            roomId,
            newOwners: mergedWall.assignedRoomIds
          });
        }
        break;

      case 'create_shared':
        // Create new shared wall group
        const sharedGroup = sharedWallSystem.createOrUpdateSharedWallGroup(
          newWall,
          roomId,
          collisionResult.existingWalls
        );
        sharedWallGroups.push(sharedGroup);
        processedWalls.push(sharedGroup.primaryWall);
        break;

      default:
        // Fallback - create wall with ownership
        const fallbackWall: WallWithTempId = {
          ...newWall,
          originatingRoomId: roomId,
          assignedRoomIds: [roomId]
        };
        processedWalls.push(fallbackWall);
    }
  }

  console.log('✅ CONNECTING ROOM PROCESSING COMPLETE:', {
    roomId,
    processedWallCount: processedWalls.length,
    sharedGroupsCreated: sharedWallGroups.length,
    coordinateCollisions
  });

  return {
    processedWalls,
    sharedWallGroups,
    coordinateCollisions
  };
}

/**
 * Enhanced resize operation that respects shared wall ownership
 */
export function resizeWallWithOwnershipRespect(
  wallToResize: WallWithTempId,
  newStart: Point,
  newEnd: Point,
  roomId: string,
  allWalls: WallWithTempId[]
): WallWithTempId {
  // Check if this wall is part of a shared group
  const sharedGroup = sharedWallSystem.getSharedWallGroup(wallToResize.tempId || '');
  
  if (sharedGroup && sharedGroup.ownerRoomIds.length > 1) {
    console.log('🔄 RESIZING SHARED WALL:', {
      wallId: wallToResize.tempId,
      groupId: sharedGroup.groupId,
      ownerRooms: sharedGroup.ownerRoomIds,
      requestingRoom: roomId
    });
    
    // For shared walls, ensure the requesting room has permission to resize
    if (!sharedGroup.ownerRoomIds.includes(roomId)) {
      console.warn('⚠️ UNAUTHORIZED RESIZE ATTEMPT:', {
        wallId: wallToResize.tempId,
        requestingRoom: roomId,
        authorizedRooms: sharedGroup.ownerRoomIds
      });
      return wallToResize; // Return unchanged
    }
  }

  // Apply resize with ownership preservation
  const resizedWall: WallWithTempId = {
    ...wallToResize,
    start: { ...newStart },
    end: { ...newEnd }
  };

  console.log('✅ WALL RESIZED WITH OWNERSHIP RESPECT:', {
    wallId: wallToResize.tempId,
    roomId,
    isSharedWall: !!sharedGroup,
    ownerRooms: wallToResize.assignedRoomIds
  });

  return resizedWall;
}